import { useState } from 'react'
import { TrendingUp, TrendingDown, AlertCircle, Activity, Database, Clock, Zap, BarChart3, Filter, Heart, Play, CalendarClock } from 'lucide-react'
import { useAnalytics } from '../hooks/useAnalytics'

function formatDuration(seconds) {
  if (!seconds && seconds !== 0) return '—';
  const s = Math.round(seconds);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (m < 60) return rem > 0 ? `${m}m ${rem}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const mins = m % 60;
  return mins > 0 ? `${h}h ${mins}m` : `${h}h`;
}

const EMPTY = {
  usageByCustomer: [],
  slaMetrics: [],
  topFailures: [],
  slowestExecutions: [],
  mostActiveItems: [],
  highestVolumeItems: [],
  workspaceMetrics: [],
  trends: {},
  errorCategories: [],
  customerHealth: [],
  currentlyRunning: [],
  overdueScheduled: [],
}

export default function Analytics() {
  const [timeRange, setTimeRange] = useState('30')
  const [customerFilter, setCustomerFilter] = useState('all')
  const [workspaceFilter, setWorkspaceFilter] = useState('all')
  const [itemTypeFilter, setItemTypeFilter] = useState('all')

  const { data: analytics = EMPTY, isLoading } = useAnalytics(timeRange, customerFilter)

  const customers = [...new Set([
    ...analytics.slaMetrics.map(m => m.customer_name),
    ...analytics.workspaceMetrics.map(m => m.customer_name)
  ])].filter(Boolean).sort()

  const workspaces = [...new Set(
    analytics.workspaceMetrics
      .filter(m => customerFilter === 'all' || m.customer_name === customerFilter)
      .map(m => m.workspace_name)
  )].filter(Boolean).sort()

  const filterData = (data, customerField = 'customer_name', workspaceField = 'workspace_name', itemTypeField = 'item_type') => {
    return data.filter(item => {
      if (customerFilter !== 'all' && item[customerField] !== customerFilter) return false
      if (workspaceFilter !== 'all' && workspaceField && item[workspaceField] !== workspaceFilter) return false
      if (itemTypeFilter !== 'all' && itemTypeField && item[itemTypeField] !== itemTypeFilter) return false
      return true
    })
  }

  if (isLoading) {
    return <div className="loading">Loading analytics...</div>
  }

  const filteredUsageByCustomer = filterData(analytics.usageByCustomer || [], 'customer_name', null, null)
  const filteredSlaMetrics = filterData(analytics.slaMetrics || [])
  const filteredWorkspaceMetrics = filterData(analytics.workspaceMetrics || [])
  const filteredTopFailures = filterData(analytics.topFailures || [])
  const filteredSlowExecutions = filterData(analytics.slowestExecutions || [])
  const filteredActiveItems = filterData(analytics.mostActiveItems || [])
  const filteredVolumeItems = filterData(analytics.highestVolumeItems || [])
  const filteredOverdue = filterData(analytics.overdueScheduled || [], 'customer_name', 'workspace_name', null)
  const filteredRunning = filterData(analytics.currentlyRunning || [], 'customer_name', 'workspace_name', 'item_type')

  const filteredErrorCategories = (analytics.errorCategories || [])
    .filter(item => customerFilter === 'all' || item.customer_name === customerFilter)
    .reduce((acc, item) => {
      const existing = acc.find(cat => cat.category === item.category)
      if (existing) existing.count += item.count
      else acc.push({ category: item.category, count: item.count })
      return acc
    }, [])
    .sort((a, b) => b.count - a.count)

  const healthOrder = { critical: 0, warning: 1, no_data: 2, healthy: 3 }
  const sortedHealth = [...(analytics.customerHealth || [])]
    .filter(c => customerFilter === 'all' || c.customer_name === customerFilter)
    .sort((a, b) => healthOrder[a.health_status] - healthOrder[b.health_status])

  return (
    <div className="analytics">
      <div className="page-header">
        <h1>Analytics</h1>
        <select value={timeRange} onChange={(e) => setTimeRange(e.target.value)} className="time-range-select">
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
        </select>
      </div>

      {/* Filters */}
      <div className="analytics-filters">
        <div className="filter-icon"><Filter size={18} /><span>Filters:</span></div>
        <div className="filter-group">
          <label>Customer:</label>
          <select value={customerFilter} onChange={(e) => { setCustomerFilter(e.target.value); setWorkspaceFilter('all') }}>
            <option value="all">All Customers</option>
            {customers.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label>Workspace:</label>
          <select value={workspaceFilter} onChange={(e) => setWorkspaceFilter(e.target.value)}>
            <option value="all">All Workspaces</option>
            {workspaces.map(ws => <option key={ws} value={ws}>{ws}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label>Type:</label>
          <select value={itemTypeFilter} onChange={(e) => setItemTypeFilter(e.target.value)}>
            <option value="all">All Types</option>
            <option value="notebook">Notebooks</option>
            <option value="datapipeline">Pipelines</option>
          </select>
        </div>
      </div>

      {/* Customer Health Overview */}
      {sortedHealth.length > 0 && (
        <div className="analytics-card full-width" style={{ marginBottom: '1.5rem' }}>
          <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
            <Heart size={20} /> Customer Health (Last 7 days)
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '1rem' }}>
            {sortedHealth.map((c, i) => (
              <HealthCard key={i} customer={c} />
            ))}
          </div>
        </div>
      )}

      {/* Currently Running */}
      {filteredRunning.length > 0 && (
        <div className="analytics-card full-width" style={{ marginBottom: '1.5rem', borderLeft: '4px solid var(--blue-500, #3b82f6)' }}>
          <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
            <Play size={20} color="#3b82f6" />
            Currently Running ({filteredRunning.length})
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {filteredRunning.map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1rem', background: 'var(--gray-50, #f9fafb)', borderRadius: '6px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={{ fontSize: '0.75rem', padding: '2px 8px', borderRadius: '12px', background: '#dbeafe', color: '#1d4ed8', fontWeight: 500 }}>
                    {item.item_type === 'datapipeline' ? 'PIPELINE' : 'NOTEBOOK'}
                  </span>
                  <div>
                    <strong>{item.item_name || 'Unknown'}</strong>
                    <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>{item.customer_name} / {item.workspace_name}</div>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: 600, color: '#3b82f6' }}>{item.running_minutes}m</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>started {new Date(item.started_at).toLocaleTimeString()}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Overdue Scheduled */}
      {filteredOverdue.length > 0 && (
        <div className="analytics-card full-width" style={{ marginBottom: '1.5rem', borderLeft: '4px solid #f59e0b' }}>
          <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
            <CalendarClock size={20} color="#f59e0b" />
            Overdue Scheduled Items ({filteredOverdue.length})
          </h2>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--gray-200)' }}>
                  <th style={{ textAlign: 'left', padding: '0.5rem', color: 'var(--gray-500)', fontWeight: 500 }}>Customer</th>
                  <th style={{ textAlign: 'left', padding: '0.5rem', color: 'var(--gray-500)', fontWeight: 500 }}>Item</th>
                  <th style={{ textAlign: 'left', padding: '0.5rem', color: 'var(--gray-500)', fontWeight: 500 }}>Workspace</th>
                  <th style={{ textAlign: 'left', padding: '0.5rem', color: 'var(--gray-500)', fontWeight: 500 }}>Schedule</th>
                  <th style={{ textAlign: 'left', padding: '0.5rem', color: 'var(--gray-500)', fontWeight: 500 }}>Last Run</th>
                  <th style={{ textAlign: 'left', padding: '0.5rem', color: 'var(--gray-500)', fontWeight: 500 }}>Overdue By</th>
                </tr>
              </thead>
              <tbody>
                {filteredOverdue.map((item, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                    <td style={{ padding: '0.75rem 0.5rem' }}>{item.customer_name}</td>
                    <td style={{ padding: '0.75rem 0.5rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ fontSize: '0.7rem', padding: '1px 6px', borderRadius: '10px', background: 'var(--gray-100)', color: 'var(--gray-600)' }}>
                          {item.item_type === 'datapipeline' ? 'PIPELINE' : 'NOTEBOOK'}
                        </span>
                        <strong>{item.item_name}</strong>
                      </div>
                    </td>
                    <td style={{ padding: '0.75rem 0.5rem', color: 'var(--gray-600)' }}>{item.workspace_name}</td>
                    <td style={{ padding: '0.75rem 0.5rem' }}>
                      <span style={{ fontSize: '0.75rem', padding: '2px 8px', borderRadius: '12px', background: '#fef3c7', color: '#92400e' }}>
                        {item.schedule_frequency || 'scheduled'}
                      </span>
                    </td>
                    <td style={{ padding: '0.75rem 0.5rem', color: item.last_run ? 'var(--gray-600)' : '#ef4444' }}>
                      {item.last_run ? new Date(item.last_run).toLocaleString() : 'Never ran'}
                    </td>
                    <td style={{ padding: '0.75rem 0.5rem' }}>
                      <span style={{ fontWeight: 600, color: '#ef4444' }}>
                        {item.hours_since_run != null
                          ? item.hours_since_run >= 24
                            ? `${Math.floor(item.hours_since_run / 24)}d ${item.hours_since_run % 24}h`
                            : `${item.hours_since_run}h`
                          : '—'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Trend Indicators */}
      {analytics.trends && (
        <div className="trends-section">
          <h2><BarChart3 size={20} /> Trends vs Previous Period {customerFilter === 'all' ? '(All Customers)' : `(${customerFilter})`}</h2>
          <div className="trends-grid">
            <TrendCard label="Total Executions" change={analytics.trends.executions_change} icon={<Activity size={24} />} />
            <TrendCard label="Failures" change={analytics.trends.failures_change} icon={<AlertCircle size={24} />} inverse />
            <TrendCard label="Avg Duration" change={analytics.trends.duration_change} icon={<Clock size={24} />} inverse />
          </div>
        </div>
      )}

      <div className="analytics-grid">
        <div className="analytics-card">
          <h2><TrendingUp size={20} /> API Usage by Customer</h2>
          <div className="usage-table">
            <table>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Total Calls</th>
                  <th>Avg Response Time</th>
                  <th>Error Rate</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsageByCustomer.length === 0 ? (
                  <tr><td colSpan="4" style={{ textAlign: 'center', padding: '2rem', color: 'var(--gray-600)' }}>No data available</td></tr>
                ) : (
                  filteredUsageByCustomer.map((item, i) => (
                    <tr key={i}>
                      <td>{item.customer_name}</td>
                      <td>{item.total_requests?.toLocaleString()}</td>
                      <td>{item.avg_response_time_ms}ms</td>
                      <td><span className={item.error_rate > 5 ? 'error-high' : 'error-low'}>{item.error_rate}%</span></td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="analytics-card">
          <h2><AlertCircle size={20} /> SLA Metrics & Execution Breakdown</h2>
          <div className="sla-table">
            <table>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Total Runs</th>
                  <th>Success Rate</th>
                  <th>📓 Notebooks</th>
                  <th>🔄 Pipelines</th>
                  <th>⏰ Scheduled</th>
                  <th>👤 Manual</th>
                  <th>Rows Processed</th>
                </tr>
              </thead>
              <tbody>
                {filteredSlaMetrics.length === 0 ? (
                  <tr><td colSpan="8" style={{ textAlign: 'center', padding: '2rem', color: 'var(--gray-600)' }}>No data available</td></tr>
                ) : (
                  filteredSlaMetrics.map((item, i) => (
                    <tr key={i}>
                      <td>{item.customer_name}</td>
                      <td>{item.total_runs}</td>
                      <td>
                        <span className={`sla-badge ${item.success_rate >= 95 ? 'good' : 'warning'}`}>
                          {item.success_rate.toFixed(1)}%
                        </span>
                      </td>
                      <td>{item.notebook_executions}</td>
                      <td>{item.pipeline_executions}</td>
                      <td>{item.scheduled_executions}</td>
                      <td>{item.manual_executions}</td>
                      <td>
                        {item.total_rows_processed > 0
                          ? <span className="row-count"><Database size={14} />{item.total_rows_processed.toLocaleString()}</span>
                          : '-'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {filteredWorkspaceMetrics.length > 0 && (
        <div className="analytics-card full-width" style={{ marginBottom: '1.5rem' }}>
          <h2><Activity size={20} /> Workspace-Level Metrics</h2>
          <div className="workspace-table">
            <table>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Workspace</th>
                  <th>Total Executions</th>
                  <th>Failures</th>
                  <th>Success Rate</th>
                  <th>Avg Duration</th>
                </tr>
              </thead>
              <tbody>
                {filteredWorkspaceMetrics.map((item, i) => (
                  <tr key={i}>
                    <td>{item.customer_name}</td>
                    <td><strong>{item.workspace_name}</strong></td>
                    <td>{item.total_executions}</td>
                    <td><span className={item.failures > 0 ? 'failure-count' : ''}>{item.failures}</span></td>
                    <td>
                      <span className={`sla-badge ${item.success_rate >= 95 ? 'good' : 'warning'}`}>
                        {item.success_rate.toFixed(1)}%
                      </span>
                    </td>
                    <td>{formatDuration(item.avg_duration)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="analytics-grid three-col">
        <div className="analytics-card">
          <h2><Clock size={20} /> Slowest Executions</h2>
          <div className="performance-list">
            {filteredSlowExecutions.length === 0 ? (
              <p className="empty-state">No data available</p>
            ) : (
              filteredSlowExecutions.slice(0, 8).map((item, i) => (
                <div key={i} className="performance-item">
                  <div className="item-header">
                    <span className="item-type-badge">{item.item_type === 'notebook' ? '📓' : '🔄'}</span>
                    <div className="item-info">
                      <strong>{item.item_name}</strong>
                      <small>{item.customer_name} / {item.workspace_name}</small>
                    </div>
                  </div>
                  <div className="item-stats">
                    <span className="stat-value slow">{formatDuration(item.avg_duration)}</span>
                    <span className="stat-label">{item.execution_count} run{item.execution_count !== 1 ? 's' : ''}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="analytics-card">
          <h2><Zap size={20} /> Most Active Items</h2>
          <div className="performance-list">
            {filteredActiveItems.length === 0 ? (
              <p className="empty-state">No data available</p>
            ) : (
              filteredActiveItems.slice(0, 8).map((item, i) => (
                <div key={i} className="performance-item">
                  <div className="item-header">
                    <span className="item-type-badge">{item.item_type === 'notebook' ? '📓' : '🔄'}</span>
                    <div className="item-info">
                      <strong>{item.item_name}</strong>
                      <small>{item.customer_name} / {item.workspace_name}</small>
                    </div>
                  </div>
                  <div className="item-stats">
                    <span className="stat-value active">{item.execution_count}</span>
                    <span className="stat-label">
                      {item.failure_count > 0 && <span className="failure-badge">{item.failure_count} failures</span>}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="analytics-card">
          <h2><Database size={20} /> Highest Data Volume</h2>
          <div className="performance-list">
            {filteredVolumeItems.length === 0 ? (
              <p className="empty-state">No data available</p>
            ) : (
              filteredVolumeItems.slice(0, 8).map((item, i) => (
                <div key={i} className="performance-item">
                  <div className="item-header">
                    <span className="item-type-badge">{item.item_type === 'notebook' ? '📓' : '🔄'}</span>
                    <div className="item-info">
                      <strong>{item.item_name}</strong>
                      <small>{item.customer_name} / {item.workspace_name}</small>
                    </div>
                  </div>
                  <div className="item-stats">
                    <span className="stat-value volume">{item.total_rows.toLocaleString()}</span>
                    <span className="stat-label">{item.execution_count} run{item.execution_count !== 1 ? 's' : ''}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {filteredErrorCategories.length > 0 && (
        <div className="analytics-card full-width" style={{ marginBottom: '1.5rem' }}>
          <h2><AlertCircle size={20} /> Failure Analysis by Error Type</h2>
          <div className="error-categories">
            {filteredErrorCategories.map((cat, i) => (
              <div key={i} className="error-category-item">
                <div className="category-name">{cat.category}</div>
                <div className="category-count">{cat.count} failures</div>
                <div className="category-bar">
                  <div
                    className="category-bar-fill"
                    style={{ width: `${(cat.count / Math.max(...filteredErrorCategories.map(c => c.count))) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="analytics-card full-width">
        <h2>Top Failures (Last {timeRange} days)</h2>
        <div className="failures-list">
          {filteredTopFailures.length === 0 ? (
            <p className="empty-state">No failures recorded</p>
          ) : (
            filteredTopFailures.map((failure, i) => (
              <div key={i} className="failure-item">
                <div className="failure-header">
                  <strong>{failure.customer_name}</strong>
                  <span className="failure-count">{failure.count} failure{failure.count !== 1 ? 's' : ''}</span>
                </div>
                <div className="failure-details">
                  <p>
                    <strong>Item:</strong> {failure.item_name}
                    {failure.workspace_name && <span> / {failure.workspace_name}</span>}
                  </p>
                  <p className="error-message">{failure.error_message}</p>
                  <p className="failure-time">Last: {new Date(failure.last_occurrence).toLocaleString()}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}

function HealthCard({ customer }) {
  const colors = {
    healthy:  { bg: '#f0fdf4', border: '#22c55e', badge: '#dcfce7', badgeText: '#166534', label: 'Healthy' },
    warning:  { bg: '#fffbeb', border: '#f59e0b', badge: '#fef3c7', badgeText: '#92400e', label: 'Warning' },
    critical: { bg: '#fef2f2', border: '#ef4444', badge: '#fee2e2', badgeText: '#991b1b', label: 'Critical' },
    no_data:  { bg: 'var(--gray-50)', border: 'var(--gray-300)', badge: 'var(--gray-100)', badgeText: 'var(--gray-600)', label: 'No Data' }
  }
  const c = colors[customer.health_status] || colors.no_data
  return (
    <div style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: '8px', padding: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
        <strong style={{ fontSize: '0.9rem' }}>{customer.customer_name}</strong>
        <span style={{ fontSize: '0.7rem', padding: '2px 8px', borderRadius: '10px', background: c.badge, color: c.badgeText, fontWeight: 600 }}>
          {c.label}
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.25rem', fontSize: '0.8rem', color: 'var(--gray-600)' }}>
        <span>Runs (7d):</span><span style={{ fontWeight: 600 }}>{customer.total_runs_7d}</span>
        <span>Failures:</span>
        <span style={{ fontWeight: 600, color: customer.failures_7d > 0 ? '#ef4444' : 'inherit' }}>
          {customer.failures_7d}
        </span>
        <span>Success rate:</span>
        <span style={{ fontWeight: 600 }}>
          {customer.success_rate_7d != null ? `${customer.success_rate_7d.toFixed(1)}%` : '—'}
        </span>
        <span>Last activity:</span>
        <span style={{ fontWeight: 500 }}>
          {customer.last_activity ? new Date(customer.last_activity).toLocaleDateString() : 'Never'}
        </span>
      </div>
    </div>
  )
}

function TrendCard({ label, change, icon, inverse = false }) {
  if (change === null || change === undefined) {
    return (
      <div className="trend-card">
        <div className="trend-icon">{icon}</div>
        <div className="trend-content">
          <div className="trend-label">{label}</div>
          <div className="trend-value neutral" style={{ fontSize: '1rem', color: 'var(--gray-400)' }}>N/A — no baseline</div>
        </div>
      </div>
    )
  }

  const isPositive = inverse ? change < 0 : change > 0
  const isNegative = inverse ? change > 0 : change < 0

  return (
    <div className="trend-card">
      <div className="trend-icon">{icon}</div>
      <div className="trend-content">
        <div className="trend-label">{label}</div>
        <div className={`trend-value ${isPositive ? 'positive' : isNegative ? 'negative' : 'neutral'}`}>
          {change > 0 ? <TrendingUp size={18} /> : change < 0 ? <TrendingDown size={18} /> : null}
          {Math.abs(change)}%
        </div>
      </div>
    </div>
  )
}
